import type { BbQueueManager } from '@Components/BigBrowse/bb2/BbQueueManager';
import type { FloorDM } from '@Model/FloorModels';
import { getFloorSpecFromJourney } from '@Model/FloorModels';
import { ObservableProp, useClassStore } from '@Utils/useClassStore';
import { inject, injectable } from 'inversify';
import { computed, observable } from 'mobx';

import { FloorsListGateway } from '~gateways/floors-list';
import container from '~ioc/ioc';
import { getFloorStore } from '~stores/floor';
import type { FloorStore } from '~stores/floor';

@injectable()
export class FloorsListStore {
  @inject(FloorsListGateway) private _floorsGateway!: FloorsListGateway;
  bbQueueManager = new ObservableProp<BbQueueManager | undefined>(undefined); // Solves circular dependency injection

  isFetching = new ObservableProp(false);
  floorsDM = new ObservableProp<FloorDM[]>([]);
  defaultFloorId = new ObservableProp<FloorDM['id'] | undefined>(undefined);
  isReady = new ObservableProp(false);

  private _floorsRegistry = observable.map<FloorDM['id'], FloorStore>();

  @computed get activeFloorSpecs(): FloorDM['id'] | undefined {
    if (!this.bbQueueManager.value?.root?.journeyParams) {
      return undefined;
    }

    const { journey, journeyParam } =
      this.bbQueueManager.value?.root.journeyParams;

    return getFloorSpecFromJourney({ journey, journeyParam });
  }

  @computed get activeFloorStore(): FloorStore | undefined {
    if (!this.activeFloorSpecs) {
      return undefined;
    }

    return this.getFloorStore(this.activeFloorSpecs);
  }

  fetch = async () => {
    this.isFetching.set(true);
    try {
      const data = await this._floorsGateway.getFloors();
      this.floorsDM.set(data.floors);
      this.defaultFloorId.set(data.defaultFloor);
      this._floorsRegistry.clear();
      this.isReady.set(true);
    } finally {
      this.isFetching.set(false);
    }
  };

  getFloorStore = (id: FloorDM['id']): FloorStore =>
    this._floorsRegistry.get(id) ?? this._createFloorStore(id);

  private _createFloorStore = (id: FloorDM['id']): FloorStore => {
    const floorData = this.floorsDM.value.find((floor) => floor.id === id);
    if (!floorData) {
      throw new Error(
        'Tried to initialize a FloorStore with id that was not found'
      );
    }

    const floorStore = getFloorStore(floorData);
    this._floorsRegistry.set(id, floorStore);
    floorStore.fetch().then();

    return floorStore;
  };
}

export const useFloorsStore2 = () =>
  useClassStore<FloorsListStore>(container.get(FloorsListStore));
